import { create } from "zustand";
import type { User, RegistrationStatus } from "@/types";
import { authApi } from "@/api/auth";

const DEFAULT_AUTH_CHECK_TIMEOUT_MS = 10_000;

function parsePositiveTimeout(value: string | undefined, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export const AUTH_CHECK_TIMEOUT_MS = parsePositiveTimeout(
  import.meta.env.VITE_AUTH_CHECK_TIMEOUT_MS,
  DEFAULT_AUTH_CHECK_TIMEOUT_MS
);

function rejectAfterTimeout<T>(promise: Promise<T>, timeoutMs: number) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error("Authentication check timed out"));
    }, timeoutMs);
  });

  return Promise.race([promise, timeout]).finally(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  });
}

interface AuthState {
  user: User | null;
  status: RegistrationStatus | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setUser: (user: User, status: RegistrationStatus) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  status: null,
  isLoading: true,
  isAuthenticated: false,

  login: async (email: string, password: string) => {
    set({ isLoading: true });
    try {
      const response = await authApi.login({ email, password });
      set({
        user: response.user,
        status: response.status,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors
    } finally {
      set({
        user: null,
        status: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },

  checkAuth: async () => {
    set({ isLoading: true });
    try {
      const response = await rejectAfterTimeout(
        authApi.getMe(),
        AUTH_CHECK_TIMEOUT_MS
      );
      set({
        user: response.user,
        status: response.status,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      set({
        user: null,
        status: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },

  setUser: (user: User, status: RegistrationStatus) => {
    set({
      user,
      status,
      isAuthenticated: true,
    });
  },

  clearAuth: () => {
    set({
      user: null,
      status: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },
}));
